<svelte:options
  customElement={{
    tag: "oc-tab-bar-v1",
    extend: window.__components.extend(),
    props: {
      iconPosition: { type: "String", reflect: true, attribute: "icon-position" },
      selected: { type: "Number", reflect: false },
    },
  }}
/>

<script lang="ts">
  import { onMount } from "svelte";
  import { onReady } from "@otto-ec/global-resources/dom";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";
  import ArrowButtonV1 from "../../../common/components/ArrowButton/ArrowButtonV1.svelte";
  import type { Methods, Props } from "./TabBarV1.types";
  import { useTabInitialization } from "./composables/useTabInitialization.svelte";
  import { useScrollManagement } from "./composables/useScrollManagement.svelte";
  import { useIndicatorPosition } from "./composables/useIndicatorPosition.svelte";
  import { useKeyboardNavigation } from "./composables/useKeyboardNavigation.svelte";
  import { useTabSelection } from "./composables/useTabSelection.svelte";
  import { useSmoothScroll } from "./composables/useSmoothScroll.svelte";
  import { useHistoryTracking } from "./composables/useHistoryTracking.svelte";

  /*                                                         */
  let { iconPosition = "left", selected }: Props = $props();

  const Host = $host();

  const tabInit = useTabInitialization();
  const scrollManager = useScrollManagement();
  const indicatorManager = useIndicatorPosition();
  const keyboardNav = useKeyboardNavigation();
  const tabSelector = useTabSelection();
  const smoothScroll = useSmoothScroll();
  const historyTracking = useHistoryTracking();

  let tabs = $state<HTMLElement[]>([]);
  let panels = $state<HTMLElement[]>([]);
  let selectedIndex = $state(0);
  let tabbarElement = $state<HTMLElement | null>(null);
  let tabsContainerElement = $state<HTMLElement | null>(null);
  let panelsContainerElement = $state<HTMLElement | null>(null);
  let indicatorElement = $state<HTMLElement | null>(null);

  const canScrollLeft = $derived(scrollManager.canScrollLeft());
  const canScrollRight = $derived(scrollManager.canScrollRight());
  const isInitialized = $derived(indicatorManager.isInitialized());
  const scrollLeft = $derived(scrollManager.scrollLeft());

  function updateIndicatorPosition(): void {
    indicatorManager.updateIndicatorPosition(
      indicatorElement,
      tabsContainerElement,
      tabs,
      selectedIndex,
      scrollLeft,
    );
  }

  function selectTab(index: number, initiallySelected?: boolean, fromHistory = false): void {
    tabSelector.selectTab(tabs, panels, index, (newIndex) => {
      selectedIndex = newIndex;
      scrollManager.scrollToTab(tabsContainerElement, tabs, newIndex);
      updateIndicatorPosition();
      /*                                                              */
      /*                                                                        */
      if (!initiallySelected && !fromHistory) {
        historyTracking.updateUrlForTab(tabs, newIndex);
      }

      if (panelsContainerElement && panels.length > 0) {
        const container = panelsContainerElement;
        requestAnimationFrame(() => {
          const scrollPosition = newIndex * container.clientWidth;
          smoothScroll.scrollToElement(container, scrollPosition, initiallySelected);
        });
      }
    });
  }

  function handleScroll(): void {
    scrollManager.updateScrollState(tabsContainerElement);
    updateIndicatorPosition();
  }

  function handleTabSelect(event: CustomEvent): void {
    tabSelector.handleTabSelect(event, selectTab);
  }

  function handleKeydown(event: KeyboardEvent): void {
    keyboardNav.handleKeydown(event, tabs, selectedIndex, selectTab);
  }

  function handlePanelsKeydown(event: KeyboardEvent): void {
    /*                                                     */
    /*                                                  */
    if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
      event.preventDefault();
    }
  }

  onMount(() => {
    tabs = Array.from(Host.querySelectorAll("oc-tab-item-v1"));
    panels = Array.from(Host.querySelectorAll("oc-tab-container-v1"));

    tabInit.initializePanels(panels, tabs);
    tabInit.initializeTabs(tabs, panels);

    /*                                                                              */
    const urlTabIndex = historyTracking.getInitialTabFromUrl(tabs);

    /*                               */
    let initialIndex: number;
    let initialSelected = true;

    if (urlTabIndex !== null && !tabs[urlTabIndex]?.hasAttribute("disabled")) {
      initialIndex = urlTabIndex;
    } else if (selected !== undefined && selected >= 0 && selected < tabs.length) {
      /*                                            */
      if (!tabs[selected]?.hasAttribute("disabled")) {
        initialIndex = selected;
      } else {
        initialIndex = keyboardNav.findFirstEnabledTab(tabs);
      }
    } else {
      /*                           */
      initialIndex = keyboardNav.findFirstEnabledTab(tabs);
    }

    selectTab(initialIndex, initialSelected);

    const removeHistoryListener = historyTracking.setupHistoryListener(tabs, (index) => {
      selectTab(index, false, true);
    });

    Host.addEventListener("oc-tab-select", handleTabSelect as EventListener);

    /*                                                                            */
    if (tabsContainerElement) {
      tabsContainerElement.addEventListener("scroll", handleScroll, { passive: true });
      scrollManager.updateScrollState(tabsContainerElement);
      scrollManager.recalculateScrollPoints(tabsContainerElement, tabs);
    }

    /*                                                  */
    const resizeObserver = new ResizeObserver(() => {
      scrollManager.updateScrollState(tabsContainerElement);
      scrollManager.recalculateScrollPoints(tabsContainerElement, tabs);
      updateIndicatorPosition();
    });

    if (tabbarElement) {
      resizeObserver.observe(tabbarElement);
    }
    if (tabsContainerElement) {
      resizeObserver.observe(tabsContainerElement);
    }

    /*                                               */
    /*                                                                 */
    requestAnimationFrame(() => {
      selectTab(initialIndex, initialSelected);
    });

    return () => {
      removeHistoryListener();
      Host.removeEventListener("oc-tab-select", handleTabSelect as EventListener);
      if (tabsContainerElement) {
        tabsContainerElement.removeEventListener("scroll", handleScroll);
      }
      resizeObserver.disconnect();
    };
  });

  $effect(() => {
    updateIndicatorPosition();
  });

  export const select: Methods["select"] = (index: number) => {
    if (typeof index === "number" && index >= 0 && index < tabs.length) {
      selectTab(index);
    } else {
      console.warn(`Invalid tab index: ${index}. Must be between 0 and ${tabs.length - 1}`);
    }
  };
</script>

<div
  bind:this={tabbarElement}
  class="tabbar"
  class:tabbar--fade-left={canScrollLeft}
  class:tabbar--fade-right={canScrollRight}
>
  <!-- Scrollable tabs container -->
  <!-- svelte-ignore a11y_interactive_supports_focus -->
  <div
    bind:this={tabsContainerElement}
    class="tabbar__tabs"
    role="tablist"
    aria-orientation="horizontal"
    onkeydown={handleKeydown}
  >
    <div
      bind:this={indicatorElement}
      class="tabbar__indicator"
      class:tabbar__indicator--animated={isInitialized}
    ></div>
    <slot />
  </div>

  {#if $onReady}
    <!-- Left scroll arrow -->
    <ArrowButtonV1
      hidden={!canScrollLeft}
      direction="left"
      size="50"
      onclick={() => scrollManager.scrollLeftButton(tabsContainerElement, tabs)}
    />
    <!-- Right scroll arrow -->
    <ArrowButtonV1
      hidden={!canScrollRight}
      direction="right"
      size="50"
      onclick={() => scrollManager.scrollRightButton(tabsContainerElement, tabs)}
    />
  {/if}
</div>

<!-- svelte-ignore a11y_no_noninteractive_tabindex -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  bind:this={panelsContainerElement}
  class="tabbar__panels"
  tabindex="0"
  onkeydown={handlePanelsKeydown}
>
  <slot name="container" />
</div>

<DefaultStyles />

<style lang="scss">
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "@otto-ec/otto-components-utils/scss/mixins";

  :host {
    display: block;
    --arrow-button-position: 50%;
  }

  :global {
    .tabbar {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      position: relative;
      outline: none;
      gap: 0;
      padding: 0 tokens.$oc-component-tab-bar-spacing-x;
    }

    .tabbar__panels {
      display: flex;
      width: 100%;
      overflow-x: hidden; /*                                  */
      overflow-y: visible;
      scroll-snap-type: x mandatory;
      scrollbar-width: none; /*      */
      scroll-behavior: smooth;

      /*                                                                         */
      touch-action: pan-y; /*                           */
      overscroll-behavior-x: none; /*                                  */

      &::-webkit-scrollbar {
        display: none; /*                */
      }

      /*                                    */
      @supports (scroll-snap-type: x mandatory) {
        scroll-snap-type: x mandatory;
      }

      /*                                    */
      @supports (scroll-behavior: smooth) {
        scroll-behavior: smooth;
      }

      /*                                    */
      @supports (scroll-behavior: smooth) and (scroll-snap-type: x mandatory) {
        scroll-behavior: smooth;
        scroll-snap-type: x mandatory;
      }

      @media (prefers-reduced-motion) {
        scroll-behavior: auto;
      }

      &.oc-floating-focus-v1-hide-outline {
        outline: none;
      }
    }

    .tabbar__tabs {
      flex: 1;
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      gap: tokens.$oc-component-tab-bar-gap-x;
      overflow-x: auto;
      overflow-y: hidden;
      scrollbar-width: none; /*      */
      scroll-behavior: smooth;
      position: relative;
      /*                                                                                             */
      padding: 0 tokens.$oc-component-interactive-overlay-default-offset;

      /*             */
      &::-webkit-scrollbar {
        display: none; /*                */
      }

      /*                                */
      @media (prefers-reduced-motion) {
        scroll-behavior: auto;
      }
    }

    /*                                                                     */
    .tabbar--fade-left::before {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: tokens.$oc-component-tab-bar-gradient-width;
      background: tokens.$oc-component-tab-bar-gradient-color-left;
      pointer-events: none;
      z-index: 1;
    }

    .tabbar--fade-right::after {
      content: "";
      position: absolute;
      right: 0;
      top: 0;
      bottom: 0;
      width: tokens.$oc-component-tab-bar-gradient-width;
      background: tokens.$oc-component-tab-bar-gradient-color-right;
      pointer-events: none;
      z-index: 1;
    }

    /*                                                    */
    .tabbar .arrow-button {
      z-index: 2;
    }

    .tabbar__indicator {
      position: absolute;
      left: 0;
      bottom: 0;
      height: tokens.$oc-component-tab-item-selected-indicator-width;
      background-color: tokens.$oc-component-tab-item-selected-indicator-color;
      pointer-events: none;
      z-index: 1;
    }

    @media (prefers-reduced-motion: no-preference) {
      .tabbar__indicator--animated {
        transition:
          transform tokens.$oc-component-tab-bar-transition-duration
            tokens.$oc-component-tab-bar-transition-easing,
          width tokens.$oc-component-tab-bar-transition-duration
            tokens.$oc-component-tab-bar-transition-easing,
          bottom tokens.$oc-component-tab-bar-transition-duration
            tokens.$oc-component-tab-bar-transition-easing;
      }
    }
  }
</style>
